import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { FC } from 'react';

import { IconComplete, IconIncomplete, IconInProgress } from '../icons';

const buttonStyles = cva(
  'flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 typography-button-sm text-neutral-300',
  {
    variants: {
      variant: {
        complete: ['hover:bg-neutral-900', 'active:bg-neutral-900', 'focus:bg-neutral-900'],
        'in-progress': ['hover:bg-neutral-900', 'active:bg-neutral-900', 'focus:bg-neutral-900'],
        incomplete: ['hover:bg-neutral-900', 'active:bg-neutral-900', 'focus:bg-neutral-900'],
      },
      isSelected: {
        true: 'bg-neutral-900 text-secondary-300',
        false: '',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'incomplete',
      disabled: false,
      isSelected: false,
    },
  },
);

interface InvoiceStepButtonProps {
  variant: 'complete' | 'in-progress' | 'incomplete';
  label: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  isSelected?: boolean;
}

const getIconColor = (variant: InvoiceStepButtonProps['variant'], isSelected: boolean) => {
  if (isSelected) return 'text-secondary-300';
  switch (variant) {
    case 'complete':
      return 'text-accent-verdigris-500';
    case 'in-progress':
      return 'text-accent-selective-yellow-500';
    case 'incomplete':
      return 'text-neutral-500';
  }
};

const getIconComponent = (variant: InvoiceStepButtonProps['variant'], isSelected: boolean) => {
  const color = getIconColor(variant, isSelected);
  const baseStyles = 'h-4 w-4';
  switch (variant) {
    case 'complete':
      return <IconComplete className={clsx(color, baseStyles)} />;
    case 'in-progress':
      return <IconInProgress className={clsx(color, baseStyles)} />;
    case 'incomplete':
      return <IconIncomplete className={clsx(color, baseStyles)} />;
  }
};

export const InvoiceStepButton: FC<InvoiceStepButtonProps> = ({
  variant,
  label,
  onClick,
  className,
  disabled = false,
  isSelected = false,
}) => {
  const icon = getIconComponent(variant, isSelected);

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={buttonStyles({ variant, disabled, isSelected, className })}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
};
